import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { debounce } from '@theme/utilities';

class BrindeLimitManager {
  static SOURCE_ID = 'brinde-limit';

  #debouncedCheck;
  #isProcessing = false;
  #isPendingCheck = false;
  #notificationTimeout = null;
  #noticeActiveUntil = 0;
  #lastKnownCart = null;
  #autoAddVariantIds = new Set();
  // Saved before any other script can patch window.fetch
  #originalFetch = window.fetch.bind(window);

  // Restores data-brinde-limit-exceeded if morphSection wipes it during notice window
  #noticeObserver = new MutationObserver((mutations) => {
    if (Date.now() >= this.#noticeActiveUntil) return;
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-brinde-limit-exceeded' &&
        !mutation.target.hasAttribute('data-brinde-limit-exceeded')
      ) {
        mutation.target.setAttribute('data-brinde-limit-exceeded', '');
      }
    }
  });

  // Restores data-brinde-checking if morphSection wipes it while check is pending
  #checkingObserver = new MutationObserver((mutations) => {
    if (!this.#isPendingCheck && !this.#isProcessing) return;
    for (const mutation of mutations) {
      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-brinde-checking' &&
        !mutation.target.hasAttribute('data-brinde-checking')
      ) {
        mutation.target.setAttribute('data-brinde-checking', '');
      }
    }
  });

  constructor() {
    this.#debouncedCheck = debounce(this.#runCheck.bind(this), 300);
    this.#loadAutoAddVariantIds();
  }

  #loadAutoAddVariantIds() {
    try {
      const configEl = document.getElementById('gift-auto-add-config');
      if (!configEl) return;
      const config = JSON.parse(configEl.textContent || '{}');
      this.#autoAddVariantIds = new Set(
        (config.gifts || []).map((g) => Number(g.variantId)).filter(Boolean)
      );
    } catch {
      // ignore parse errors
    }
  }

  init() {
    // Must run before any cart add can happen so the interceptor is in place
    this.#installFetchInterceptor();

    // Synchronous: marks check as pending and CSS-disables immediately
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleUpdateImmediate.bind(this));
    // Debounced: fallback enforcement for server-side additions that bypass the interceptor
    document.addEventListener(ThemeEvents.cartUpdate, this.#debouncedCheck);
    // Capture-phase click: blocks checkout button before yampiClick() fires
    document.addEventListener('click', this.#handleCheckoutClick.bind(this), true);
    // Capture-phase submit: final guard for keyboard/programmatic submissions
    document.addEventListener('submit', this.#handleFormSubmit.bind(this), true);

    this.#checkOnInit();
  }

  // Replaces window.fetch to intercept /cart/add.js and /cart/change.js before they reach Shopify
  #installFetchInterceptor() {
    const self = this;
    const originalFetch = this.#originalFetch;

    window.fetch = async function (resource, init) {
      const url =
        typeof resource === 'string'
          ? resource
          : resource instanceof Request
            ? resource.url
            : '';

      if (url.includes('/cart/add')) {
        const blocked = await self.#shouldBlockAdd(init ?? {});
        if (blocked) {
          self.#showDrawerNotice();
          return self.#limitErrorResponse();
        }
      }

      if (url.includes('/cart/change')) {
        const result = await self.#shouldCapChange(init ?? {});
        if (result === 'block') {
          self.#showDrawerNotice();
          return self.#limitErrorResponse();
        }
        if (result !== null) {
          // Let through with a capped quantity
          return originalFetch(resource, result);
        }
      }

      return originalFetch(resource, init);
    };
  }

  #limitErrorResponse() {
    const config = this.#getConfig();
    const message = config?.message || 'Você atingiu o limite de brindes permitidos no carrinho.';
    return new Response(JSON.stringify({ status: 422, description: message }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  // Returns true if the add should be blocked (brinde items would exceed the limit)
  async #shouldBlockAdd(init) {
    try {
      const config = this.#getConfig();
      if (!config) return false;

      const limit = typeof config.limit === 'number' ? config.limit : 5;
      const knownGiftVariantIds = new Set((config.giftVariantIds || []).map(Number));

      const items = this.#parseAddBody(init.body);
      if (!items.length) return false;

      const brindesAdding = items.filter((item) => this.#isAddItemBrinde(item, knownGiftVariantIds));
      if (!brindesAdding.length) return false;

      const addingQty = brindesAdding.reduce((sum, i) => sum + (Number(i.quantity) || 1), 0);

      const cart = await this.#fetchCart();
      if (!cart?.items) return false;

      const currentCount = this.#countGifts(cart, knownGiftVariantIds);
      return currentCount + addingQty > limit;
    } catch {
      return false;
    }
  }

  // Returns null (allow), 'block', or a modified init with a capped quantity
  async #shouldCapChange(init) {
    try {
      const config = this.#getConfig();
      if (!config) return null;

      const limit = typeof config.limit === 'number' ? config.limit : 5;
      const knownGiftVariantIds = new Set((config.giftVariantIds || []).map(Number));

      const change = this.#parseChangeBody(init.body);
      if (!change || change.quantity == null) return null;

      const cart = this.#lastKnownCart ?? (await this.#fetchCart());
      if (!cart?.items) return null;

      // Find the item being changed by line index or by key/variant id
      let item = null;
      if (change.line != null) {
        item = cart.items[change.line - 1] ?? null;
      } else if (change.id != null) {
        item =
          cart.items.find(
            (i) => i.key === change.id || String(i.variant_id) === String(change.id)
          ) ?? null;
      }

      if (!item || !this.#isGiftItem(item, knownGiftVariantIds)) return null;

      // Only intervene when increasing quantity
      if (change.quantity <= item.quantity) return null;

      const totalGifts = this.#countGifts(cart, knownGiftVariantIds);
      const newTotal = totalGifts - item.quantity + change.quantity;

      if (newTotal <= limit) return null;

      const maxQty = limit - (totalGifts - item.quantity);

      if (maxQty <= 0) return 'block';

      // Cap to the maximum allowed quantity and let through
      return { ...init, body: JSON.stringify({ ...change, quantity: maxQty }) };
    } catch {
      return null;
    }
  }

  // Parses the fetch body from a /cart/add.js request into a normalized item array
  #parseAddBody(body) {
    if (!body) return [];
    try {
      let parsed;

      if (typeof body === 'string') {
        parsed = JSON.parse(body);
      } else if (body instanceof FormData) {
        const giftProp = body.get('properties[_gobeautexCartGift]');
        return [
          {
            id: Number(body.get('id')),
            quantity: Number(body.get('quantity')) || 1,
            properties: giftProp ? { _gobeautexCartGift: giftProp } : {},
          },
        ];
      } else {
        return [];
      }

      if (Array.isArray(parsed.items)) {
        return parsed.items.map((i) => ({
          id: Number(i.id),
          quantity: Number(i.quantity) || 1,
          properties: i.properties || {},
        }));
      }

      if (parsed.id) {
        return [
          {
            id: Number(parsed.id),
            quantity: Number(parsed.quantity) || 1,
            properties: parsed.properties || {},
          },
        ];
      }

      return [];
    } catch {
      return [];
    }
  }

  // Parses the fetch body from a /cart/change.js request
  #parseChangeBody(body) {
    if (!body) return null;
    try {
      if (typeof body === 'string') return JSON.parse(body);
      if (body instanceof FormData) {
        const result = {};
        if (body.has('line')) result.line = Number(body.get('line'));
        if (body.has('id')) result.id = body.get('id');
        if (body.has('quantity')) result.quantity = Number(body.get('quantity'));
        return result;
      }
      return null;
    } catch {
      return null;
    }
  }

  // Detects if an item being added is a brinde: by property or known variant ID
  #isAddItemBrinde(item, knownGiftVariantIds) {
    if (item.properties?._gobeautexCartGift) return true;
    if (knownGiftVariantIds.has(Number(item.id))) return true;
    return false;
  }

  // Fires synchronously on every CartUpdateEvent — sets pending flag and CSS-locks
  #handleUpdateImmediate(event) {
    const source = event?.detail?.data?.source;
    if (source === BrindeLimitManager.SOURCE_ID) return;
    this.#isPendingCheck = true;
    this.#setCSSBlock(true);
  }

  // Capture-phase click interceptor — runs before onclick="yampiClick()" on the button
  #handleCheckoutClick(event) {
    const btn = event.target.closest('#checkout');
    if (!btn) return;
    if (this.#isPendingCheck || this.#isProcessing) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  // Capture-phase form submit guard — covers keyboard/programmatic submissions
  #handleFormSubmit(event) {
    if (event.target?.id !== 'cart-form') return;

    if (this.#isPendingCheck || this.#isProcessing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Fallback: check cached cart state
    if (this.#lastKnownCart?.items) {
      const config = this.#getConfig();
      if (!config) return;
      const limit = typeof config.limit === 'number' ? config.limit : 5;
      const knownGiftVariantIds = new Set((config.giftVariantIds || []).map(Number));
      if (this.#countGifts(this.#lastKnownCart, knownGiftVariantIds) > limit) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.#isProcessing) this.#checkCart();
      }
    }
  }

  // Debounced: fallback removal for server-side additions that bypass the fetch interceptor
  async #runCheck(event) {
    if (this.#isProcessing) return;
    const source = event?.detail?.data?.source;
    if (source === BrindeLimitManager.SOURCE_ID) return;
    await this.#checkCart();
  }

  // Called once on init to enforce limit against the current cart state
  async #checkOnInit() {
    const config = this.#getConfig();
    if (!config) return;
    const cart = await this.#fetchCart();
    if (!cart?.items?.length) return;
    const limit = typeof config.limit === 'number' ? config.limit : 5;
    const knownGiftVariantIds = new Set((config.giftVariantIds || []).map(Number));
    if (this.#countGifts(cart, knownGiftVariantIds) > limit) {
      await this.#checkCart();
    }
  }

  async #checkCart() {
    const config = this.#getConfig();
    if (!config) {
      this.#clearPending();
      return;
    }

    const limit = typeof config.limit === 'number' ? config.limit : 5;
    const knownGiftVariantIds = new Set((config.giftVariantIds || []).map(Number));

    this.#isProcessing = true;
    this.#setCSSBlock(true);

    try {
      let cart = await this.#fetchCart();
      if (!cart?.items?.length) return;

      const totalGiftQty = this.#countGifts(cart, knownGiftVariantIds);
      if (totalGiftQty <= limit) return;

      this.#showDrawerNotice();

      let safety = 0;
      while (cart && safety < 10) {
        safety++;
        const allGiftItems = cart.items.filter((item) =>
          this.#isGiftItem(item, knownGiftVariantIds)
        );
        const total = allGiftItems.reduce((sum, i) => sum + i.quantity, 0);
        if (total <= limit) break;

        const removable = allGiftItems.filter((item) => !this.#isAutoAddGift(item));
        if (removable.length === 0) break;

        const lastGift = removable[removable.length - 1];
        const lineIndex = cart.items.findIndex((i) => i.key === lastGift.key);
        if (lineIndex === -1) break;

        const toRemove = Math.min(lastGift.quantity, total - limit);
        const newQty = lastGift.quantity - toRemove;

        cart = await this.#changeByLine(lineIndex + 1, newQty);
        if (!cart || cart.status || cart.errors) break;
      }

      if (cart && !cart.status && !cart.errors) {
        document.dispatchEvent(
          new CartUpdateEvent(cart, BrindeLimitManager.SOURCE_ID, {
            source: BrindeLimitManager.SOURCE_ID,
            itemCount: cart.item_count ?? 0,
          })
        );
      }
    } catch (error) {
      console.error('brinde-limit:', error);
    } finally {
      this.#clearPending();
    }
  }

  #clearPending() {
    this.#isProcessing = false;
    this.#isPendingCheck = false;
    this.#setCSSBlock(false);
  }

  #setCSSBlock(active) {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    if (active) {
      drawer.setAttribute('data-brinde-checking', '');
      // Watch for morphSection wiping the attribute while check is still running
      this.#checkingObserver.disconnect();
      this.#checkingObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['data-brinde-checking'],
      });
    } else {
      this.#checkingObserver.disconnect();
      drawer.removeAttribute('data-brinde-checking');
    }
  }

  #showDrawerNotice() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;

    drawer.open?.();

    this.#noticeActiveUntil = Date.now() + 5000;
    drawer.setAttribute('data-brinde-limit-exceeded', '');

    this.#noticeObserver.disconnect();
    this.#noticeObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['data-brinde-limit-exceeded'],
    });

    clearTimeout(this.#notificationTimeout);
    this.#notificationTimeout = setTimeout(() => {
      this.#noticeActiveUntil = 0;
      drawer.removeAttribute('data-brinde-limit-exceeded');
      this.#noticeObserver.disconnect();
    }, 5000);
  }

  #countGifts(cart, knownGiftVariantIds) {
    return cart.items.reduce((sum, item) => {
      return this.#isGiftItem(item, knownGiftVariantIds) ? sum + item.quantity : sum;
    }, 0);
  }

  // Detects brindes in cart items: by known variant ID, property, or URL path segment
  #isGiftItem(item, knownGiftVariantIds) {
    if (knownGiftVariantIds.has(Number(item.variant_id))) return true;
    if (item.properties?._gobeautexCartGift) return true;
    if (item.url?.toLowerCase().includes('/brinde')) return true;
    return false;
  }

  #isAutoAddGift(item) {
    return (
      Boolean(item.properties?._gobeautexCartGift) &&
      this.#autoAddVariantIds.has(Number(item.variant_id))
    );
  }

  #getConfig() {
    try {
      const el = document.getElementById('cart-brinde-limit-config');
      if (!el) return null;
      return JSON.parse(el.textContent || '{}');
    } catch {
      return null;
    }
  }

  async #fetchCart() {
    const res = await this.#originalFetch('/cart.js');
    if (!res.ok) return null;
    const cart = await res.json();
    this.#lastKnownCart = cart;
    return cart;
  }

  async #changeByLine(line, quantity) {
    const res = await this.#originalFetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    const cart = await res.json();
    if (!cart.status && !cart.errors) this.#lastKnownCart = cart;
    return cart;
  }
}

const manager = new BrindeLimitManager();
manager.init();
